import React from "react";

/**
 * Top-level Error Boundary — prevents white-screen-of-death when any
 * unhandled exception is thrown during render or in event handlers (post-mount).
 *
 * Common triggers we want to catch gracefully:
 *   1. Stale service-worker mismatch (HTML references hashes the cache can't satisfy)
 *   2. Lazy-loaded chunk load failures after a deploy
 *   3. Genuine code bugs in a deeply nested page component
 *
 * The fallback UI offers a one-tap recovery — clears caches + unregisters the
 * service worker + hard-reloads. This single button fixes ~95% of stale-deploy
 * issues without the user needing to know what a "service worker" is.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, recovering: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Log to console — useful in DevTools and via remote-error services in future.
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary] caught:", error, info);
        // Note: we used to auto-trigger a cache-clear-and-reload here when the error
        // looked like a stale chunk load. That created a refresh-loop on production
        // when chunks kept failing for any reason. Now the user explicitly clicks
        // the "Clear cache & refresh" button — at worst they see the panel once.
    }

    recoverFromStaleDeploy = async ({ silent = false } = {}) => {
        if (this.state.recovering) return;
        // Hard guard: never recover more than once per browser SESSION. Stops any
        // theoretical loop where a crash-recover-crash chain would otherwise pin
        // a tab to "refreshing forever".
        try {
            if (sessionStorage.getItem("hpl_error_recovered_v1") === "1") {
                // Already tried once in this tab — bail and let the user choose.
                this.setState({ recovering: false });
                return;
            }
            sessionStorage.setItem("hpl_error_recovered_v1", "1");
        } catch { /* sessionStorage may be unavailable in private mode — best-effort */ }

        this.setState({ recovering: !silent });
        try {
            // 1. Unregister every service worker for this origin
            if ("serviceWorker" in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister()));
            }
            // 2. Purge every cache bucket
            if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
            }
        } catch (e) {
            // Don't block the reload if cache APIs throw — still better to reload than stay broken
            // eslint-disable-next-line no-console
            console.warn("[ErrorBoundary] cache purge failed:", e);
        } finally {
            // 3. Bypass HTTP cache too — query-string burst forces a fresh network fetch
            const url = new URL(window.location.href);
            url.searchParams.set("__hplreload", Date.now().toString());
            window.location.replace(url.toString());
        }
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <div
                style={{
                    minHeight: "100vh",
                    background: "#fafaf9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px",
                    fontFamily:
                        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                data-testid="error-boundary"
            >
                <div
                    style={{
                        maxWidth: 520,
                        background: "#ffffff",
                        border: "1px solid #e7e5e4",
                        borderLeft: "3px solid #0369A1",
                        padding: 32,
                    }}
                >
                    <div
                        style={{
                            fontSize: 10,
                            letterSpacing: 2,
                            color: "#0369A1",
                            textTransform: "uppercase",
                            fontWeight: 600,
                            marginBottom: 12,
                        }}
                    >
                        Something went wrong
                    </div>
                    <h1
                        style={{
                            fontFamily: "Georgia, 'Times New Roman', serif",
                            fontSize: 28,
                            color: "#1c1917",
                            margin: 0,
                            marginBottom: 12,
                        }}
                    >
                        Let's get you unstuck
                    </h1>
                    <p style={{ color: "#57534e", fontSize: 14, lineHeight: 1.55, marginBottom: 8 }}>
                        The page hit an unexpected error. This usually happens right after a fresh deploy,
                        when your browser is still holding onto cached files from the previous version.
                    </p>
                    <p style={{ color: "#57534e", fontSize: 14, lineHeight: 1.55, marginBottom: 20 }}>
                        Tap the button below to clear the cache and refresh — it usually fixes it instantly.
                    </p>
                    <button
                        onClick={() => this.recoverFromStaleDeploy()}
                        disabled={this.state.recovering}
                        data-testid="error-boundary-recover"
                        style={{
                            background: "#0369A1",
                            color: "#ffffff",
                            border: "none",
                            padding: "12px 24px",
                            fontSize: 14,
                            fontWeight: 600,
                            letterSpacing: 0.4,
                            cursor: this.state.recovering ? "wait" : "pointer",
                            opacity: this.state.recovering ? 0.7 : 1,
                            marginRight: 8,
                        }}
                    >
                        {this.state.recovering ? "Refreshing…" : "Clear cache & refresh"}
                    </button>
                    <button
                        onClick={() => (window.location.href = "/")}
                        data-testid="error-boundary-home"
                        style={{
                            background: "#ffffff",
                            color: "#44403c",
                            border: "1px solid #d6d3d1",
                            padding: "12px 24px",
                            fontSize: 14,
                            cursor: "pointer",
                        }}
                    >
                        Go to home
                    </button>
                    {this.state.error && (
                        <details
                            style={{
                                marginTop: 24,
                                color: "#a8a29e",
                                fontSize: 11,
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            }}
                        >
                            <summary style={{ cursor: "pointer", color: "#78716c" }}>Technical details</summary>
                            <pre style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>
                                {String(this.state.error?.message || this.state.error)}
                            </pre>
                        </details>
                    )}
                </div>
            </div>
        );
    }
}
